import React, { useEffect, useState } from 'react';
import Button from 'antd/lib/button';
import Checkbox from 'antd/lib/checkbox';

import LabelSelector from 'components/label-selector/label-selector';
import CVATTooltip from 'components/common/cvat-tooltip';
import { plural, t } from 'utils/i18n';
import {
    Candidate, detectionOf, parseCandidates, resolveModel, StateLike,
} from './references/reference-model';
import LibraryThumb from './references/library-thumb';
import ModelPicker from './references/model-picker';
import type { PinKey } from './preview-mode';
import './styles.scss';

// the attributes whose value is a library file with a preview
export const PREVIEW_SPEC_NAMES = ['blend_model', 'spot_model', 'curtain_model', 'tule_model'];
export const CARD_WIDTH = 260;

interface SpecLike { id: number; name: string; values?: string[] }
export interface CardPreview {
    specId: number;
    name: string | null;
    imageUrl: string | null;
    fullImageUrl: string | null;
}

function previewSpecs(state: StateLike): SpecLike[] {
    const specs = state.label.attributes as SpecLike[];
    return PREVIEW_SPEC_NAMES.flatMap((name) => specs.filter((spec) => spec.name === name));
}

export function hasPreviewSpec(state: StateLike): boolean {
    return state.objectType === 'shape' && previewSpecs(state).length > 0;
}

function parseValue(item: string): Candidate[] {
    try {
        return parseCandidates([JSON.parse(item)]);
    } catch {
        return [];
    }
}

// spot, curtain and tule models are listed by the label, one JSON object per value; blend models per shape
function candidatesOf(state: StateLike, spec: SpecLike): Candidate[] {
    const own = parseCandidates(state.select_values?.[spec.id]);
    return own.length ? own : (spec.values ?? []).flatMap(parseValue);
}

export function previewsOf(state: StateLike, backendAPI: string): CardPreview[] {
    return previewSpecs(state).map((spec) => {
        const value = (state.attributes[spec.id] ?? '').trim();
        const model = resolveModel({ value, candidates: candidatesOf(state, spec) }, backendAPI);
        return {
            specId: spec.id,
            name: model?.name ?? null,
            imageUrl: model?.imageUrl ?? null,
            fullImageUrl: model?.fullImageUrl ?? null,
        };
    });
}

// a reload renews clientIDs, so a saved shape is followed by its server id
export function pinnedState<T extends StateLike>(states: T[], key: PinKey | null): T | null {
    if (!key) return null;
    if (key.serverID !== null) return states.find((state) => state.serverID === key.serverID) ?? null;
    return states.find((state) => state.clientID === key.clientID) ?? null;
}

// a picked model goes to the pinned shape, or to every shape of its label on the drawing
export function modelTargets<T extends StateLike>(states: T[], state: T, all: boolean): T[] {
    return all ? states.filter((item) => hasPreviewSpec(item) && item.label.id === state.label.id) : [state];
}

export function keyOf(state: StateLike): PinKey {
    return { clientID: state.clientID, serverID: typeof state.serverID === 'number' ? state.serverID : null };
}

// In the Превью tab a right click on a shape with a preview pins its card instead of the CVAT
// menu; a left click on empty canvas, Esc or another frame unpins it.
export function usePinTriggers(
    target: HTMLElement | null,
    enabled: boolean,
    frame: number,
    resolve: (clientID: number) => StateLike | null,
    pin: (key: PinKey | null) => void,
): void {
    useEffect(() => pin(null), [frame, pin]);
    useEffect(() => {
        if (!target || !enabled) return undefined;
        // captured above the canvas, so the CVAT menu listening on it never sees the event
        const above = target.parentElement ?? target;
        const onContextMenu = (event: MouseEvent): void => {
            const shape = (event.target as Element).closest('[clientID]');
            const state = shape ? resolve(Number(shape.getAttribute('clientID'))) : null;
            if (!state || !hasPreviewSpec(state)) {
                pin(null);
                return;
            }
            event.stopPropagation();
            event.preventDefault();
            pin(keyOf(state));
        };
        const onMouseDown = (event: MouseEvent): void => {
            if ((event.target as Element).tagName === 'svg' && event.button === 0) pin(null);
        };
        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') pin(null);
        };
        above.addEventListener('contextmenu', onContextMenu, true);
        target.addEventListener('mousedown', onMouseDown);
        window.addEventListener('keydown', onKeyDown);
        return () => {
            above.removeEventListener('contextmenu', onContextMenu, true);
            target.removeEventListener('mousedown', onMouseDown);
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [target, enabled, resolve, pin]);
}

interface ModelCardProps {
    state: StateLike;
    previews: CardPreview[];
    labels: any[];
    top: number;
    left: number;
    scale: number;
    angle: number;
    noMatches: boolean;
    onChangeLabel(label: any): void;
    onChange: (() => void) | null; // null when the models are changed in the Objects tab only
}

export function ModelCard(props: ModelCardProps): JSX.Element {
    const {
        state, previews, labels, top, left, scale, angle, noMatches, onChangeLabel, onChange,
    } = props;
    const hasModel = previews.some((preview) => preview.name !== null);
    return (
        <div
            className='cvat-model-card'
            style={{
                top, left, width: CARD_WIDTH, transform: `scale(${scale}) rotate(${angle}deg)`,
            }}
        >
            <div className='cvat-model-card-previews'>
                {previews.map((preview) => (
                    <LibraryThumb
                        key={preview.specId}
                        className='cvat-references-model-thumb'
                        src={preview.imageUrl}
                        fullSrc={preview.fullImageUrl}
                    />
                ))}
            </div>
            <div className='cvat-model-card-body'>
                <div className='cvat-model-card-head'>
                    <span className='cvat-model-card-id'>{state.clientID}</span>
                    <CVATTooltip title={t('Change current label')}>
                        <LabelSelector
                            size='small'
                            className='cvat-model-card-label'
                            labels={labels}
                            value={state.label.id}
                            onChange={onChangeLabel}
                        />
                    </CVATTooltip>
                </div>
                {previews.map((preview) => (
                    <span
                        key={preview.specId}
                        className={`cvat-model-card-name${preview.name ? '' : ' cvat-model-card-name-empty'}`}
                        title={preview.name ?? undefined}
                    >
                        {preview.name ?? t('No model yet')}
                    </span>
                ))}
                {noMatches && <span className='cvat-model-card-note'>{t('No matches from the photos')}</span>}
                {onChange && !noMatches && (
                    <Button type='link' size='small' className='cvat-model-card-change' onClick={onChange}>
                        {hasModel ? t('Change') : t('Pick')}
                    </Button>
                )}
            </div>
        </div>
    );
}

interface DrawingModelPickerProps {
    state: StateLike;
    backendAPI: string;
    sameLabel: number; // shapes of its label on the drawing, itself included
    all: boolean; // "for all of them" starts checked
    onSetValue(specId: number, value: string, all: boolean): void;
    onClose(): void;
}

// the photo picks of the drawing shape, without a search: its pool comes from the photos
export function DrawingModelPicker(props: DrawingModelPickerProps): JSX.Element | null {
    const {
        state, backendAPI, sameLabel, all: startAll, onSetValue, onClose,
    } = props;
    const [all, setAll] = useState(startAll && sameLabel > 1);
    const detection = detectionOf(state);
    if (!detection) return null;
    return (
        <ModelPicker
            detection={detection}
            crop={null}
            backendAPI={backendAPI}
            busy={false}
            onUse={(candidate) => onSetValue(detection.specId, candidate.path, all)}
            onRemove={() => onSetValue(detection.specId, '', all)}
            onClose={onClose}
            subtitle={t('Picked on the photos of this task: {count}', {
                count: plural(detection.candidates.length, 'model'),
            })}
            allowCurrent={all}
            extra={sameLabel > 1 && (
                <Checkbox checked={all} onChange={(event) => setAll(event.target.checked)}>
                    {t('For all {label} on this drawing ({count})', { label: state.label.name, count: sameLabel })}
                </Checkbox>
            )}
        />
    );
}
